import { getCountryLanguage } from '@repo/utils';

import type { ModalType } from '@/components/modals/asset-modal.types';

import type { ConfigSource, TranslationSource } from '@/utils/app.utils';

export type TelemetryAttribute = string | number | boolean;

export const TELEMETRY_LOGS = {
    AppBootstrap: 'ot.app.bootstrap',
    AppInit: 'ot.app.init',
    AssetModifyRedirectedToCart: 'ot.asset.modify.redirected_to_cart',
    AssetPaymentDateChangeFailed: 'ot.asset.payment_date.change_failed',
    AssetShare: 'ot.asset.share',
    ConnectionStatusChanged: 'ot.connection.status_changed',
    CoworkerPreferencesMissingBuCode: 'ot.coworker_preferences.bucode_missing',
    InvalidateAsset: 'ot.api.invalidateAsset',
    ModalOpened: 'ot.modal.opened',
    RefreshClicked: 'ot.refresh.clicked',
} as const;

interface AppTelemetryAttributes {
    country?: string;
    environment?: string;
    isCoworkerHub?: boolean;
    language?: string;
    online?: boolean;
}

interface ConfigTelemetryAttributes {
    buCode?: string;
    configSource?: ConfigSource;
    translationSource?: TranslationSource;
}

interface BootstrapTelemetryAttributes {
    completed?: boolean;
}

export interface AssetTelemetryAttributes {
    id?: string;
    orderType?: string;
    status?: string;
    type?: string;
    viewContext?: 'confirmation' | 'details';
}

interface ConnectionTelemetryAttributes {
    status: 'offline' | 'online';
}

interface RefreshTelemetryAttributes {
    source: 'asset_list' | 'asset_details';
}

interface CoworkerPreferencesTelemetryAttributes {
    hasCoworkerProfile: boolean;
    hasCoworkerPreferences: boolean;
    hasBuCode: boolean;
}

interface ShareTelemetryAttributes {
    result?: ShareResult;
    shareType?: ShareType;
}

export interface ModalOpenedTelemetryAttributes {
    modal: {
        name: ModalType;
    };
}

export type ShareType = 'email' | 'sms' | 'print';

export type ShareResult = 'success' | 'failed';

export interface AssetShareTelemetryAttributes {
    share: {
        result: ShareResult;
        shareType: ShareType;
    };
}

export interface InvalidateAssetTelemetryAttributes {
    assetId: string;
    assetType: string;
}

export interface AssetPaymentDateChangeFailedTelemetryAttributes {
    orderNoSource: string;
    paymentDate: string;
}

export interface TelemetryAttributes {
    app?: AppTelemetryAttributes;
    asset?: AssetTelemetryAttributes;
    bootstrap?: BootstrapTelemetryAttributes;
    config?: ConfigTelemetryAttributes;
    connection?: ConnectionTelemetryAttributes;
    coworkerPreferences?: CoworkerPreferencesTelemetryAttributes;
    modal?: ModalOpenedTelemetryAttributes['modal'];
    refresh?: RefreshTelemetryAttributes;
    share?: ShareTelemetryAttributes;
    invalidateAsset?: InvalidateAssetTelemetryAttributes;
    assetPaymentDateChange?: AssetPaymentDateChangeFailedTelemetryAttributes;
}

type TelemetryAttributeMap<Map extends Record<keyof Map, TelemetryAttributes>> = Map;

export type TelemetryLogAttributesByName = TelemetryAttributeMap<{
    [TELEMETRY_LOGS.AppBootstrap]: {
        bootstrap: BootstrapTelemetryAttributes;
    };
    [TELEMETRY_LOGS.AppInit]: {
        config: ConfigTelemetryAttributes;
    };
    [TELEMETRY_LOGS.AssetModifyRedirectedToCart]: {
        asset: AssetTelemetryAttributes;
    };
    [TELEMETRY_LOGS.AssetPaymentDateChangeFailed]: {
        assetPaymentDateChange: AssetPaymentDateChangeFailedTelemetryAttributes;
    };
    [TELEMETRY_LOGS.AssetShare]: AssetShareTelemetryAttributes;
    [TELEMETRY_LOGS.ConnectionStatusChanged]: {
        connection: ConnectionTelemetryAttributes;
    };
    [TELEMETRY_LOGS.CoworkerPreferencesMissingBuCode]: {
        coworkerPreferences: CoworkerPreferencesTelemetryAttributes;
    };
    [TELEMETRY_LOGS.InvalidateAsset]: {
        invalidateAsset: InvalidateAssetTelemetryAttributes;
    };
    [TELEMETRY_LOGS.ModalOpened]: ModalOpenedTelemetryAttributes;
    [TELEMETRY_LOGS.RefreshClicked]: {
        refresh: RefreshTelemetryAttributes;
    };
}>;

export type TelemetryLogName = keyof TelemetryLogAttributesByName;

const getCommonTelemetryAttributes = (): TelemetryAttributes => {
    const app: AppTelemetryAttributes = {
        environment: String(import.meta.env.VITE_ENV),
        isCoworkerHub: globalThis.navigator.userAgent.includes('Co-worker Hub'),
        online: globalThis.navigator.onLine,
    };

    try {
        const { country, language } = getCountryLanguage();

        return {
            app: {
                ...app,
                country,
                language,
            },
        };
    } catch {
        return {
            app,
        };
    }
};

export const addCommonTelemetryAttributes = (attributes: TelemetryAttributes): TelemetryAttributes => {
    const commonAttributes = getCommonTelemetryAttributes();

    return {
        ...attributes,
        app: {
            ...commonAttributes.app,
            ...attributes.app,
        },
    };
};

export const flattenTelemetryAttributes = (
    attributesByGroup: TelemetryAttributes
): Record<string, TelemetryAttribute> => {
    const attributes: Record<string, TelemetryAttribute> = {};

    for (const [group, value] of Object.entries(attributesByGroup)) {
        if (!value || typeof value !== 'object') {
            continue;
        }

        for (const [key, attribute] of Object.entries(value)) {
            if (typeof attribute === 'string' || typeof attribute === 'number' || typeof attribute === 'boolean') {
                attributes[`${group}.${key}`] = attribute;
            }
        }
    }

    return attributes;
};
